import type { Folders, FileItem } from "@/types/types";
import { splitNameExt } from "@/lib/file";
import { localizeFolder } from "@/lib/components-logic/folders/folders-localize";
import { foldersRepository } from "../repositories/folders.repository";

const isFolder = (item: Folders | FileItem): item is Folders => "main_folder" in item;

const directusId = (item: FileItem): string | null =>
  typeof (item as any).file === "string" && (item as any).file.trim()
    ? (item as any).file
    : null;

export const foldersService = {
  fetchFolders: async (args: {
    isLoggedIn: boolean;
    tenantId: number | null | undefined;
    userLang: string;
  }): Promise<Folders[]> => {
    if (!args.isLoggedIn || !args.tenantId) return [];
    const folders = await foldersRepository.fetchByTenant(Number(args.tenantId));
    return Promise.all((folders ?? []).map((f) => localizeFolder(f, args.userLang)));
  },

  createFolder: (args: { name: string; parentFolderId: number; tenantId: number }) => {
    const name = args.name.trim();
    if (!name) return;
    return foldersRepository.create({
      name,
      main_folder: false,
      top_folder: args.parentFolderId,
      tenant: Number(args.tenantId),
      canBeDeleted: true,
    } as any);
  },

  editItem: async (args: { item: Folders | FileItem; name: string; tags?: string }) => {
    const name = args.name.trim();
    if (!name || args.item.id == null) return;

    if (isFolder(args.item)) return foldersRepository.update(Number(args.item.id), { name });

    await foldersRepository.updateFileItem(Number(args.item.id), { name, tags: args.tags ?? "" } as any);

    const id = directusId(args.item);
    if (id) await foldersRepository.updateDirectusFilename(id, `${name}.${(args.item as any).extension || "bin"}`);
  },

  moveItem: (args: { item: Folders | FileItem; destinationFolderId: number }) => {
    if (args.item.id == null) return;

    return isFolder(args.item)
      ? foldersRepository.update(Number(args.item.id), {
          top_folder: args.destinationFolderId,
          main_folder: false,
        } as any)
      : foldersRepository.updateFileItem(Number(args.item.id), {
          folder: args.destinationFolderId,
          date_last_modification: new Date().toISOString(),
        } as any);
  },

  deleteItem: async (args: { item: Folders | FileItem }) => {
    if (args.item.id == null) return;

    if (isFolder(args.item)) return foldersRepository.delete(Number(args.item.id));

    const id = directusId(args.item);
    if (id) try { await foldersRepository.removeDirectusFiles([id]); } catch {}

    return foldersRepository.deleteFileItem(Number(args.item.id));
  },

  addFilesToFolder: async (args: {
    files: File[];
    folderId: number;
    tags: string;
    tenantId: number;
  }) => {
    const payloads = await Promise.all(args.files.map(async (file) => {
      const fileId = await foldersRepository.uploadDirectusFile(file);
      const { name, extension } = splitNameExt(file.name);

      return {
        name,
        extension,
        type: file.type || "application/octet-stream",
        tags: args.tags,
        tenant: Number(args.tenantId),
        folder: args.folderId,
        file: fileId,
        date_last_modification: new Date().toISOString(),
      } as Partial<FileItem>;
    }));

    return foldersRepository.createFiles(payloads);
  },
};